import type { PrintElement, PrintJobSnapshot, TableElement } from "../core/types";
import { getPrintableContentHeightPx } from "../core/units";
import { paginateElements, type PageSegment } from "../render/paginateElements";

interface EstimatedTableChunk {
  height: number;
  rows: Array<Record<string, unknown>>;
  showHeader: boolean;
}

function cloneTableChunk(element: TableElement, chunk: EstimatedTableChunk): TableElement {
  return {
    ...element,
    rows: chunk.rows,
    showHeader: chunk.showHeader
  };
}

function finalizePage(pages: PageSegment[], currentPage: PrintElement[], manualBreakAfter: boolean): PrintElement[] {
  if (currentPage.length) {
    pages.push({
      elements: currentPage,
      manualBreakAfter
    });
  }

  return [];
}

// Split a measured table into page-sized row groups. This keeps preview output
// close to actual printing without attempting to split individual rows.
function splitTableRows(
  element: TableElement,
  rowHeights: number[],
  headerHeight: number,
  pageHeight: number,
  initialRemaining: number
): EstimatedTableChunk[] {
  const chunks: EstimatedTableChunk[] = [];
  const repeatHeader = element.repeatHeaderOnPageBreak !== false;
  let currentRows: Array<Record<string, unknown>> = [];
  let currentHeight = element.showHeader === false ? 0 : headerHeight;
  let currentShowHeader = element.showHeader !== false;
  let remaining = Math.max(initialRemaining - currentHeight, 1);

  const pushChunk = () => {
    if (!currentRows.length) {
      return;
    }

    chunks.push({
      rows: currentRows,
      showHeader: currentShowHeader,
      height: Math.max(currentHeight, 1)
    });

    currentRows = [];
    currentShowHeader = repeatHeader;
    currentHeight = currentShowHeader ? headerHeight : 0;
    remaining = Math.max(pageHeight - currentHeight, 1);
  };

  element.rows.forEach((row, index) => {
    const rowHeight = Math.max(rowHeights[index] ?? 0, 1);

    if (currentRows.length && rowHeight > remaining) {
      pushChunk();
    }

    currentRows.push(row);
    currentHeight += rowHeight;
    remaining -= Math.min(rowHeight, pageHeight);

    if (rowHeight >= pageHeight) {
      pushChunk();
    }
  });

  pushChunk();
  return chunks;
}

// Estimate preview pages using measured DOM heights. Explicit page-break rules
// always win; automatic estimation only runs inside those manual segments.
export function estimatePreviewPagination(doc: Document, snapshot: PrintJobSnapshot): PageSegment[] {
  const pageHeight = getPrintableContentHeightPx(snapshot);
  const manualSegments = paginateElements(snapshot.elements, { respectPageBreaks: true });
  const estimatedPages: PageSegment[] = [];
  let nextGlobalIndex = 0;

  manualSegments.forEach((segment) => {
    let currentPage: PrintElement[] = [];
    let remaining = pageHeight;

    segment.elements.forEach((element) => {
      const globalIndex = snapshot.elements.indexOf(element, nextGlobalIndex);
      nextGlobalIndex = globalIndex >= 0 ? globalIndex + 1 : nextGlobalIndex;
      const node = globalIndex >= 0 ? doc.querySelector<HTMLElement>(`[data-printer-index="${globalIndex}"]`) : null;
      const height = Math.max(node?.offsetHeight ?? 0, 1);

      if (element.kind === "table" && node) {
        const headerHeight = node.querySelector("thead")?.clientHeight ?? 0;
        const rowHeights = Array.from(node.querySelectorAll<HTMLTableRowElement>("tbody > tr")).map((row) => Math.max(row.clientHeight, 1));
        const firstRowHeight = rowHeights[0] ?? 1;
        const minimumTableHeight = (element.showHeader === false ? 0 : headerHeight) + firstRowHeight;

        if (currentPage.length && minimumTableHeight > remaining) {
          currentPage = finalizePage(estimatedPages, currentPage, false);
          remaining = pageHeight;
        }

        const chunks = splitTableRows(element, rowHeights, headerHeight, pageHeight, remaining);

        chunks.forEach((chunk, chunkIndex) => {
          if (chunkIndex > 0 && currentPage.length) {
            currentPage = finalizePage(estimatedPages, currentPage, false);
            remaining = pageHeight;
          }

          currentPage.push(cloneTableChunk(element, chunk));
          remaining -= Math.min(chunk.height, pageHeight);

          if (chunkIndex < chunks.length - 1 || chunk.height >= pageHeight) {
            currentPage = finalizePage(estimatedPages, currentPage, false);
            remaining = pageHeight;
          }
        });

        return;
      }

      // For non-table blocks, estimated pagination is conservative: move the
      // whole block to the next page instead of trying to split arbitrary HTML.
      if (currentPage.length && height > remaining) {
        currentPage = finalizePage(estimatedPages, currentPage, false);
        remaining = pageHeight;
      }

      currentPage.push(element);
      remaining -= Math.min(height, pageHeight);

      if (height >= pageHeight) {
        currentPage = finalizePage(estimatedPages, currentPage, false);
        remaining = pageHeight;
      }
    });

    currentPage = finalizePage(estimatedPages, currentPage, segment.manualBreakAfter);
  });

  return estimatedPages.length ? estimatedPages : [{ elements: [], manualBreakAfter: false }];
}
